import React, { ReactNode, useState } from "react";
import { ButtonBase, CircularProgress, ListItemText } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { StyledMenu, StyledMenuItem } from "./StyledMenu";
import useDebounce from "../../hooks/useDebounce.hook";
import { tesaGrey1 } from "../../styles/colors";

export interface Selectable {
  id: number;
  name: string;
}

export interface CustomDynamicContextMenuProps<T extends Selectable> {
  excludedIds?: number[];
  fetch: () => Promise<T[]>;
  onMenuItemClick: (selectable: T) => void;
  buttonContent?: ReactNode;
  sortAlphanumericBy?: keyof T;
}

function alphanumeric<T>(a: T, b: T, key: keyof T) {
  const labelA = a[key];
  const labelB = b[key];
  if (!(typeof labelA === "string" && typeof labelB === "string")) {
    return 0;
  }
  if (labelA.toLowerCase() < labelB.toLowerCase()) {
    return -1;
  }
  if (labelA.toLowerCase() > labelB.toLowerCase()) {
    return 1;
  }
  return 0;
}

function CustomDynamicContextMenu<T extends Selectable>(props: CustomDynamicContextMenuProps<T>): JSX.Element {
  const { fetch, excludedIds, onMenuItemClick, buttonContent, sortAlphanumericBy } = props;
  const [loading, setLoading] = useState(false);
  const debouncedLoading = useDebounce(loading, 500);
  const [data, setData] = useState<T[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    fetch()
      .then((value) => {
        let fetchedData = [...value];
        if (sortAlphanumericBy) {
          fetchedData.sort((a, b) => alphanumeric(a, b, sortAlphanumericBy));
        }
        excludedIds?.forEach((excludedId) => {
          fetchedData = fetchedData.filter((option) => option.id !== excludedId);
        });
        setData(fetchedData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const renderItems = () => {
    if (debouncedLoading) {
      return (
        <StyledMenuItem>
          <CircularProgress color="inherit" />
        </StyledMenuItem>
      );
    }
    if (data.length > 0) {
      return data.map((e) => {
        return (
          <StyledMenuItem
            key={`CustomDynamicContextMenu_item-${e.id}`}
            onClick={(): void => {
              onMenuItemClick(e);
              handleClose();
            }}
          >
            <ListItemText primary={e.name} />
          </StyledMenuItem>
        );
      });
    }
    return (
      <StyledMenuItem
        key="ustomDynamicContextMenu_empty"
        onClick={(): void => {
          handleClose();
        }}
      >
        <ListItemText primary="- No options - " />
      </StyledMenuItem>
    );
  };

  if (loading && !debouncedLoading) {
    return (
      <ButtonBase onClick={handleClick}>
        {buttonContent || <AddIcon fontSize="small" htmlColor={tesaGrey1} />}
      </ButtonBase>
    );
  }

  return (
    <>
      <ButtonBase onClick={handleClick}>
        {buttonContent || <AddIcon fontSize="small" htmlColor={tesaGrey1} />}
      </ButtonBase>
      <StyledMenu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        {renderItems()}
      </StyledMenu>
    </>
  );
}

export default CustomDynamicContextMenu;
